/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

const liveSearchProductsSlider = {
    /**
     * Alpine data for the product image slider
     *
     * @param {Object} product
     * @returns {Object}
     */
    productImageSlider(product) {
        const slides = this.getProductSlides(product)
        const animationClass = 'live-search-slider-peek'

        return {
            product,
            slides,
            activeIndex: 0,
            initialized: false,
            loadedSlidesSet: new Set(),
            loadingSlidesSet: new Set(),
            completedSlidesSet: new Set(),
            slidesVersion: 0,
            inputMode: 'mouse',
            scrollAnimationId: null,
            scrollUpdateId: null,
            slideWidth: 0,
            resizeObserver: null,
            boundResizeHandler: null,
            sliderTrack: null,
            sliderInnerTrack: null,
            get defaultAlt() {
                return product.imageLabel || product.name
            },
            get hasMultipleSlides() {
                return this.slides.length > 1
            },
            get hasControls() {
                return this.initialized && this.hasMultipleSlides
            },
            cacheSliderElements() {
                const track = this.$refs?.sliderTrack || null
                this.sliderTrack = track
                this.sliderInnerTrack = track?.querySelector('[data-slider-track]') || track
            },
            getSliderTrack() {
                if (!this.sliderTrack) {
                    this.cacheSliderElements()
                }

                return this.sliderTrack
            },
            getSliderInnerTrack() {
                if (!this.sliderInnerTrack) {
                    this.cacheSliderElements()
                }

                return this.sliderInnerTrack
            },
            touchSlides() {
                // Trigger Alpine reactivity for Set mutations.
                this.slidesVersion += 1
            },
            trackSlidesVersion() {
                // Access slidesVersion so Alpine tracks Set mutations.
                return this.slidesVersion
            },
            init() {
                this.watchTouchDevice((isTouchDevice) => {
                    this.inputMode = isTouchDevice ? 'touch' : 'mouse'
                })
                this.initializeSlider()
                this.initializeResizeHandling()
                this.$nextTick(() => {
                    this.cacheSliderElements()
                    this.resetSliderPosition()
                    this.handleScroll()
                    requestAnimationFrame(() => this.resetSliderPosition())
                })
            },
            initializeSlider() {
                if (this.initialized) {
                    return
                }

                this.initialized = true

                if (this.slides.length === 0 && this.placeholderImage) {
                    this.slides = [{
                        index: 0,
                        position: 1,
                        label: this.defaultAlt || null,
                        main: { url: this.placeholderImage },
                        sources: {
                            small: { url: this.placeholderImage },
                            large: { url: this.placeholderImage },
                            fallback: { url: this.placeholderImage },
                            thumbnail: null
                        }
                    }]
                }

                this.loadSlide(0)
                if (this.hasMultipleSlides) {
                    this.loadSlide(1)
                }
                this.schedulePreload(0)
            },
            /**
             * Load slide at index
             *
             * @param index
             */
            loadSlide(index) {
                if (index < 0 || index >= this.slides.length) {
                    return
                }

                let changed = false
                if (!this.loadedSlidesSet.has(index)) {
                    this.loadedSlidesSet.add(index)
                    changed = true
                }
                if (!this.completedSlidesSet.has(index) && !this.loadingSlidesSet.has(index)) {
                    this.loadingSlidesSet.add(index)
                    changed = true
                }
                if (changed) {
                    this.touchSlides()
                }
            },
            /**
             * Mark slide at index as loaded
             *
             * @param index
             */
            markSlideLoaded(index) {
                if (!this.loadingSlidesSet.has(index)) {
                    return
                }

                this.loadingSlidesSet.delete(index)
                this.completedSlidesSet.add(index)
                this.touchSlides()
            },
            /**
             * Check if slide at index is loading
             *
             * @param index
             * @returns {boolean}
             */
            isSlideLoading(index) {
                this.trackSlidesVersion()
                return this.loadingSlidesSet.has(index)
            },
            /**
             * Check if slide at index was scheduled for loading
             *
             * @param index
             * @returns {boolean}
             */
            isSlideLoaded(index) {
                this.trackSlidesVersion()
                return this.loadedSlidesSet.has(index)
            },
            /**
             * Handle load/error event for a slide image
             *
             * @param index
             */
            handleImageLoad(index) {
                if (!this.isSlideLoading(index)) {
                    return
                }
                this.markSlideLoaded(index)
            },
            /**
             * Get slide source for image at index
             *
             * @param index
             * @returns {string|*|string}
             */
            getSlideSrc(index) {
                const slide = this.slides[index]
                const hasLoaded = this.isSlideLoaded(index)

                if (!hasLoaded) {
                    return this.placeholderImage || ''
                }

                if (slide?.main?.url) {
                    return slide.main.url
                }

                return this.placeholderImage || slide?.main?.url || ''
            },
            /**
             * Get picture source for image at index
             *
             * @param index
             * @param size
             * @returns {*|string}
             */
            getPictureSource(index, size) {
                const slide = this.slides[index]
                const source = slide?.sources?.[size]

                const hasLoaded = this.isSlideLoaded(index)
                if (!hasLoaded) {
                    return ''
                }

                if (source?.url) {
                    return source.url
                }

                return this.placeholderImage
            },
            /**
             * Get picture dimension for image at index
             *
             * @param index
             * @param size
             * @param dimension
             * @returns {*|null}
             */
            getPictureDimension(index, size, dimension) {
                return this.slides[index]?.sources?.[size]?.[dimension] || null
            },
            /**
             * Get loading strategy for image at index
             *
             * @param index
             * @returns {string}
             */
            getLoadingStrategy(index) {
                return index <= 1 ? 'eager' : 'lazy'
            },
            /**
             * Preload slides adjacent to the current one when the browser is idle
             *
             * @param index
             */
            schedulePreload(index) {
                if (!this.hasMultipleSlides) {
                    return
                }

                const preload = () => {
                    [index + 2, index - 2].forEach((nextIndex) => this.loadSlide(nextIndex))
                }

                if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
                    window.requestIdleCallback(preload, { timeout: 200 })
                } else {
                    setTimeout(preload, 120)
                }
            },
            /**
             * Go to a specific slide by index
             *
             * @param index
             */
            goTo(index) {
                if (!this.initialized || !this.slides.length) {
                    return
                }

                const target = this.getTarget(index)
                if (target === this.activeIndex) {
                    return
                }

                this.activeIndex = target
                this.loadSlide(target)
                if (this.hasMultipleSlides) {
                    this.loadSlide(target + 1)
                    this.loadSlide(target - 1)
                }
                this.schedulePreload(target)

                const track = this.getSliderTrack()
                if (track && this.inputMode === 'touch') {
                    const width = this.slideWidth || track.clientWidth
                    const offset = width * target
                    this.scrollToOffset(track, offset)
                }
            },
            /**
             * Smoothly scroll the track to the given offset
             *
             * @param track
             * @param offset
             */
            scrollToOffset(track, offset) {
                if (!track) {
                    return
                }

                const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

                if (prefersReducedMotion) {
                    track.scrollLeft = offset
                    return
                }

                const start = track.scrollLeft
                const distance = offset - start
                if (Math.abs(distance) < 1) {
                    track.scrollLeft = offset
                    return
                }

                if (this.scrollAnimationId) {
                    cancelAnimationFrame(this.scrollAnimationId)
                    this.scrollAnimationId = null
                }

                const duration = 300
                let startTime = null
                const step = (timestamp) => {
                    if (!startTime) {
                        startTime = timestamp
                    }
                    const progress = Math.min((timestamp - startTime) / duration, 1)
                    const eased = 1 - Math.pow(1 - progress, 3)
                    track.scrollLeft = start + distance * eased
                    if (progress < 1) {
                        this.scrollAnimationId = requestAnimationFrame(step)
                    } else {
                        this.scrollAnimationId = null
                    }
                }

                this.scrollAnimationId = requestAnimationFrame(step)
            },
            next() {
                this.activeIndex === this.slides.length - 1
                    ? this.goTo(0)
                    : this.goTo(this.activeIndex + 1)
            },
            previous() {
                this.activeIndex === 0
                    ? this.goTo(this.slides.length - 1)
                    : this.goTo(this.activeIndex - 1)
            },
            /**
             * Handle keyboard navigation
             *
             * @param event
             */
            handleKeydown(event) {
                if (!['ArrowRight', 'ArrowLeft'].includes(event.key)) {
                    return
                }

                this.inputMode = 'keyboard'
                event.preventDefault()

                if (event.key === 'ArrowRight') {
                    this.next()
                } else {
                    this.previous()
                }
            },
            handleScroll() {
                if (this.inputMode !== 'touch') {
                    return
                }

                if (this.scrollUpdateId) {
                    return
                }

                this.scrollUpdateId = requestAnimationFrame(() => {
                    this.scrollUpdateId = null
                    this.updateActiveFromScroll()
                })
            },
            updateActiveFromScroll() {
                const track = this.getSliderTrack()
                if (!track) {
                    return
                }

                if (!this.slideWidth) {
                    this.updateSlideWidth()
                }

                if (!this.slideWidth) {
                    return
                }

                const index = Math.round(track.scrollLeft / this.slideWidth)
                const target = this.getTarget(index)
                if (target === this.activeIndex) {
                    return
                }

                this.activeIndex = target
                this.loadSlide(target)
                if (this.hasMultipleSlides) {
                    this.loadSlide(target + 1)
                    this.loadSlide(target - 1)
                }
                this.schedulePreload(target)
            },
            /**
             * Get valid target index
             *
             * @param index
             * @returns {number}
             */
            getTarget(index) {
                return Math.max(0, Math.min(index, this.slides.length - 1))
            },
            updateSlideWidth() {
                const track = this.getSliderTrack()
                if (!track) {
                    return
                }

                this.slideWidth = track.clientWidth || 0
            },
            initializeResizeHandling() {
                this.$nextTick(() => {
                    this.updateSlideWidth()

                    this.cacheSliderElements()
                    const track = this.getSliderTrack()
                    if (track && typeof ResizeObserver === 'function') {
                        this.resizeObserver = new ResizeObserver(() => this.updateSlideWidth())
                        this.resizeObserver.observe(track)
                        return
                    }

                    if (!this.boundResizeHandler) {
                        this.boundResizeHandler = () => this.updateSlideWidth()
                        window.addEventListener('resize', this.boundResizeHandler, { passive: true })
                    }
                })
            },
            skipSlider() {
                const target = this.$el.closest('li')?.querySelector('.product-item-link')

                if (target) {
                    target.focus()
                }
            },
            resetSliderPosition() {
                this.activeIndex = 0
                const track = this.getSliderTrack()
                const animationTarget = this.getSliderInnerTrack()
                if (animationTarget) {
                    animationTarget.classList.remove(animationClass)
                }
                if (this.inputMode !== 'touch') {
                    return
                }
                if (track && typeof track.scrollTo === 'function') {
                    // iOS can ignore scrollTo during snap/momentum;
                    // force scrollLeft as a fallback.
                    track.scrollTo({ left: 0 })
                }
                if (track) {
                    track.scrollLeft = 0
                }
            },
            destroy() {
                if (this.resizeObserver) {
                    this.resizeObserver.disconnect()
                    this.resizeObserver = null
                }
                if (this.boundResizeHandler) {
                    window.removeEventListener('resize', this.boundResizeHandler)
                    this.boundResizeHandler = null
                }
                if (this.scrollUpdateId) {
                    cancelAnimationFrame(this.scrollUpdateId)
                    this.scrollUpdateId = null
                }
                if (this.scrollAnimationId) {
                    cancelAnimationFrame(this.scrollAnimationId)
                    this.scrollAnimationId = null
                }
                this.sliderTrack = null
                this.sliderInnerTrack = null
                this.loadedSlidesSet.clear()
                this.loadingSlidesSet.clear()
                this.completedSlidesSet.clear()
            }
        }
    },
    /**
     * Prepare slider images for a product
     *
     * @param {Object} product
     * @returns {Array}
     */
    getProductSlides(product) {
        if (Array.isArray(product?.images?.slides) && product.images.slides.length) {
            return product.images.slides.map((slide, index) => ({
                ...slide,
                index
            }))
        }

        if (product?.images?.small || product?.images?.large || product?.images?.thumbnail) {
            return [{
                index: 0,
                position: 1,
                label: product.imageLabel || null,
                main: product.images.small ||
                    product.images.large ||
                    product.images.thumbnail ||
                    product.images.fallback,
                sources: {
                    small: product.images.small || null,
                    large: product.images.large || null,
                    thumbnail: product.images.thumbnail || null,
                    fallback: product.images.fallback || null
                }
            }]
        }

        return []
    }
}

export default liveSearchProductsSlider
